import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  StatusPill,
  Pipeline,
  STATUSES,
  LoadingState,
  ErrorState,
  EmptyState,
  SearchIcon,
} from '../../components';
import { api, useApi } from '../../api';
import type {
  OrderSummaryView,
  OrderDetailView,
  DocumentRefView,
  Paginated,
  UseApiResult,
} from '../../api';
import { OrderStatus, PaymentMethod, DocumentType } from '@jalupro/shared';
import { useT, useStatusLabel } from '../../i18n';
import dict from './Orders.i18n';

/**
 * Agent order history (master table + 440px detail drawer) — route /agent/orders.
 * Built from Screen3B_HistoryClean (design-source/screen-3-order-history.jsx),
 * clean / variant-B track. The portal layout supplies <CleanSidebar>; this screen
 * renders only the content to the right of it (topbar + filter rail + table/drawer).
 *
 * Data: GET /orders (agent data-isolated to own orders by the API), GET /orders/:id.
 */

/** Flip to true once SENDCLOUD_MODE=live on the server (stub tracking ids are synthetic). */
const SHIPPING_TRACKING_ENABLED = false;

// ── Formatting helpers ───────────────────────────────────────────────────────

const EUR = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
});

function money(cents: number): string {
  return EUR.format(cents / 100);
}

const SHORT_DATE = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' });
function shortDate(iso: string): string {
  return SHORT_DATE.format(new Date(iso));
}

/** Date with the year for the list column (e.g. "2 Jun 2026"). */
const DATE_YEAR = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
function dateWithYear(iso: string): string {
  return DATE_YEAR.format(new Date(iso));
}

const LONG_DATETIME = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
function longDateTime(iso: string): string {
  return LONG_DATETIME.format(new Date(iso)).replace(',', ' ·');
}

/** Relative age, e.g. "1h", "2d", "1w" (matches the wireframe's `age` column). */
function age(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.floor(ms / 60000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

/** Split "Company · City" the way the wireframe rendered the client cell. */
function splitClient(name: string): { company: string; city: string } {
  const idx = name.indexOf('·');
  if (idx === -1) return { company: name.trim(), city: '' };
  return { company: name.slice(0, idx).trim(), city: name.slice(idx + 1).trim() };
}

/** One-line address from a structured AddressDto (empty string when absent). */
function formatAddr(
  a:
    | {
        line1?: string;
        line2?: string | null;
        city?: string;
        province?: string | null;
        postalCode?: string;
        countryCode?: string;
      }
    | null
    | undefined,
): string {
  if (!a) return '';
  return [a.line1, a.line2, [a.postalCode, a.city].filter(Boolean).join(' '), a.province, a.countryCode]
    .filter(Boolean)
    .join(', ');
}

// ── Filter rail: All + one pill per happy-path status (+ branch statuses) ──
// Note: labels resolved at render time via t() / statusLabel() — not stored here.

const FILTER_KEYS: ReadonlyArray<OrderStatus | 'ALL'> = [
  'ALL',
  ...STATUSES.map((s) => s.key),
  OrderStatus.ON_HOLD_ADMIN_REVIEW,
];

export default function AgentOrders() {
  const t = useT(dict);
  const statusLabel = useStatusLabel();

  const [filter, setFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Agent data-isolation is enforced server-side by the JWT (own orders only),
  // so the agent list needs no explicit scope param.
  const ordersQuery = useApi(
    () => api.orders.list({ status: filter === 'ALL' ? undefined : filter, search: search || undefined }),
    [filter, search],
  );

  // Badge counts must reflect ALL statuses, not just the active filter's page, so we
  // fetch an unfiltered (search-scoped) page once and count from it.
  const countsQuery = useApi(
    () => api.orders.list({ search: search || undefined, page: 1, pageSize: 100 }),
    [search],
  );

  function filterLabel(key: OrderStatus | 'ALL'): string {
    if (key === 'ALL') return t('filterAll');
    if (key === OrderStatus.ON_HOLD_ADMIN_REVIEW) return t('filterOnHold');
    return statusLabel(key);
  }

  return (
    <div className="col grow m-scroll" style={{ overflow: 'hidden', minWidth: 0 }}>
      {/* Topbar */}
      <div
        className="row center between"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
      >
        <div className="col">
          <div className="meta">
            {ordersQuery.data
              ? t('totalCount', { n: ordersQuery.data.total })
              : t('heading')}
          </div>
          <div className="h1">{t('heading')}</div>
        </div>
        <div className="row gap-8 center">
          <div className="input" style={{ width: 220 }}>
            <SearchIcon />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('searchPlaceholder')}
              aria-label={t('searchPlaceholder')}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                font: 'inherit',
                color: 'inherit',
                width: '100%',
              }}
            />
          </div>
          <Link to="/agent/orders/new" className="btn primary" style={{ textDecoration: 'none' }}>
            {t('newOrderBtn')}
          </Link>
        </div>
      </div>

      {/* Filter rail */}
      <div
        className="row"
        style={{ padding: '10px 22px', background: '#fff', borderBottom: '1px solid var(--line)', gap: 6 }}
      >
        {FILTER_KEYS.map((key) => {
          const active = filter === key;
          const count = countFor(key, countsQuery.data);
          return (
            <button
              key={key}
              type="button"
              className="pill"
              onClick={() => setFilter(key)}
              style={{
                background: active ? 'var(--navy)' : 'var(--line-2)',
                color: active ? '#fff' : 'var(--navy-3)',
                border: 'none',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              {filterLabel(key)}
              {count != null ? ` · ${count}` : ''}
            </button>
          );
        })}
      </div>

      {/* Master / detail */}
      <div className="row grow m-stack" style={{ overflow: 'hidden', minHeight: 0 }}>
        {/* Table */}
        <div className="col grow" style={{ overflow: 'auto', minHeight: 0 }}>
          <OrdersTable
            query={ordersQuery}
            selectedId={selectedId}
            onSelect={(id) => setSelectedId(id)}
          />
        </div>

        {/* Detail drawer */}
        {selectedId && <OrderDrawer orderId={selectedId} onClose={() => setSelectedId(null)} />}
      </div>
    </div>
  );
}

/** Per-filter count from the unfiltered page (ALL = server total). */
function countFor(
  key: OrderStatus | 'ALL',
  data: Paginated<OrderSummaryView> | undefined,
): number | undefined {
  if (!data) return undefined;
  if (key === 'ALL') return data.total;
  return data.items.filter((o) => o.status === key).length;
}

// ── Master table ─────────────────────────────────────────────────────────────

function OrdersTable({
  query,
  selectedId,
  onSelect,
}: {
  query: UseApiResult<Paginated<OrderSummaryView>>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const t = useT(dict);
  const { data, loading, error, reload } = query;

  if (loading) return <LoadingState label={t('loadingOrders')} />;
  if (error) return <ErrorState message={error.message} onRetry={reload} />;
  if (!data || data.items.length === 0) {
    return <EmptyState title={t('emptyTitle')} hint={t('emptyHint')} />;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>{t('colId')}</th>
          <th>{t('colDate')}</th>
          <th>{t('colClient')}</th>
          <th>{t('colStatus')}</th>
          <th style={{ textAlign: 'right' }}>{t('colTotal')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {data.items.map((o) => {
          const selected = o.id === selectedId;
          const { company } = splitClient(o.clientName);
          return (
            <tr
              key={o.id}
              onClick={() => onSelect(o.id)}
              style={{ background: selected ? '#fff' : 'transparent', cursor: 'pointer' }}
            >
              <td className="mono" style={{ fontSize: 13 }}>
                {o.code}
              </td>
              <td className="meta">{dateWithYear(o.createdAt)}</td>
              <td>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{company}</div>
                {o.clientEmail && (
                  <div className="meta mono" style={{ fontSize: 12 }}>
                    {o.clientEmail}
                  </div>
                )}
              </td>
              <td>
                <StatusPill status={o.status} />
              </td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 500 }}>
                {money(o.totalCents)}
              </td>
              <td style={{ textAlign: 'right' }}>
                <span className="meta" style={{ textDecoration: selected ? 'underline' : 'none' }}>
                  {selected ? t('rowViewing') : t('rowOpen')}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Detail drawer (440px) ─────────────────────────────────────────────────────

const DOC_TYPE_KEYS: ReadonlyArray<{ type: DocumentType; dictKey: string }> = [
  { type: DocumentType.PROFORMA, dictKey: 'docProforma' },
  { type: DocumentType.INVOICE, dictKey: 'docInvoice' },
  { type: DocumentType.WAYBILL, dictKey: 'docWaybill' },
];

function OrderDrawer({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const t = useT(dict);
  const { data, loading, error, reload } = useApi(() => api.orders.get(orderId), [orderId]);

  return (
    <div
      style={{
        width: 440,
        flexShrink: 0,
        background: '#fff',
        borderLeft: '1px solid var(--line)',
        overflow: 'auto',
        height: '100%',
        minHeight: 0,
      }}
    >
      {loading && <LoadingState label={t('loadingOrder')} />}
      {error && <ErrorState message={error.message} onRetry={reload} />}
      {data && <OrderDrawerBody order={data} onClose={onClose} />}
    </div>
  );
}

function OrderDrawerBody({ order, onClose }: { order: OrderDetailView; onClose: () => void }) {
  const t = useT(dict);
  const { company } = splitClient(order.clientName);
  const statusNoteKey = `statusNote_${order.status}` as keyof typeof dict.en;

  const docByType = useMemo(() => {
    const m = new Map<DocumentType, DocumentRefView>();
    for (const d of order.documents) m.set(d.type, d);
    return m;
  }, [order.documents]);

  const trackingDoc = order.shipment?.labelUrl ?? null;

  const paymentMethodLabel: Record<PaymentMethod, string> = {
    [PaymentMethod.STRIPE]: t('pmCard'),
    [PaymentMethod.PAYPAL]: t('pmPayPal'),
    [PaymentMethod.IBAN]: t('pmIBAN'),
  };

  return (
    <>
      {/* Header */}
      <div
        className="row between center"
        style={{ padding: '16px 18px', borderBottom: '1px solid var(--line-2)' }}
      >
        <div className="col" style={{ minWidth: 0 }}>
          <div className="mono meta">{order.code}</div>
          <div className="h2">{company}</div>
          {order.client.emails[0] && (
            <div className="meta mono" style={{ fontSize: 12 }}>
              {order.client.emails[0]}
            </div>
          )}
          {formatAddr(order.client.billingAddress) && (
            <div className="meta" style={{ fontSize: 12 }}>
              {formatAddr(order.client.billingAddress)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('closeDrawer')}
          className="meta"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14 }}
        >
          ✕
        </button>
      </div>

      {/* Pipeline */}
      <div style={{ padding: '18px 18px' }}>
        <Pipeline status={order.status} />
        <div className="row between center mt-12">
          {/* statusNote tone kept from original STATUS_NOTE — only note text is i18n'd */}
          <span className={`pill ${STATUS_TONE[order.status]} dot`}>{t(statusNoteKey)}</span>
          <div className="meta">{t('timeAgo', { age: age(order.updatedAt) })}</div>
        </div>
      </div>

      <div className="divider" />

      {/* Facts */}
      <div className="col gap-12" style={{ padding: '14px 18px' }}>
        <Fact label={t('orderDate')} value={longDateTime(order.createdAt)} />
        <div className="row between">
          <span className="label">{t('factTotal')}</span>
          <span className="mono" style={{ fontSize: 14, fontWeight: 600 }}>
            {money(order.totalCents)}
          </span>
        </div>
        <div className="row between">
          <span className="label">{t('factPayment')}</span>
          <span className="pill blue dot">
            {t('prepaid', { method: paymentMethodLabel[order.paymentMethod] })}
          </span>
        </div>
        <div className="row between" style={{ alignItems: 'flex-start', gap: 12 }}>
          <span className="label" style={{ flexShrink: 0 }}>
            {t('factShipping')}
          </span>
          <span style={{ fontSize: 14, textAlign: 'right' }}>
            {(order.shippingSameAsBilling
              ? formatAddr(order.client.billingAddress)
              : formatAddr(order.shippingAddress) ||
                formatAddr(order.client.shippingAddress) ||
                formatAddr(order.client.billingAddress)) || '—'}
          </span>
        </div>
        {/* Tracking number + label link are hidden until the carrier integration is live:
            in stub mode they are synthetic. */}
        {SHIPPING_TRACKING_ENABLED && (
          <div className="row between">
            <span className="label">{t('factTracking')}</span>
            {order.trackingId ? (
              <span className="mono" style={{ fontSize: 14 }}>
                {order.trackingId}
              </span>
            ) : (
              <span className="meta">{t('notShippedYet')}</span>
            )}
          </div>
        )}
      </div>

      <div className="divider" />

      {/* Items */}
      <div className="col" style={{ padding: '14px 18px' }}>
        <div className="h3 mb-8">{t('itemsHeading', { count: order.items.length })}</div>
        {order.items.map((it) => (
          <div
            key={it.id}
            className="row between"
            style={{ padding: '4px 0', borderBottom: '1px solid var(--paper)' }}
          >
            <div style={{ fontSize: 14 }}>
              {it.productName} ×{it.qty}
              {it.foc && <span className="meta"> · FOC</span>}
            </div>
            <div className="mono" style={{ fontSize: 13 }}>
              {money(it.foc ? 0 : it.unitPriceCents * it.qty)}
            </div>
          </div>
        ))}
        {order.marketingPack && (
          <div
            className="row between"
            style={{ padding: '4px 0', borderBottom: '1px solid var(--paper)' }}
          >
            <div style={{ fontSize: 14 }}>{t('marketingPack')}</div>
            <div className="mono" style={{ fontSize: 13 }}>
              {money(0)}
            </div>
          </div>
        )}
      </div>

      <div className="divider" />

      {/* Documents (authored + numbered by Holded) */}
      <div className="col gap-6" style={{ padding: '14px 18px' }}>
        <div className="h3 mb-4">{t('documentsHeading')}</div>
        {DOC_TYPE_KEYS.map((row) => {
          const doc = docByType.get(row.type);
          const ready = !!doc;
          const label = t(row.dictKey);
          const name = doc?.number ? `${label} — ${doc.number}` : label;
          const when = doc?.issuedAt ? shortDate(doc.issuedAt) : t('docPending');
          const body = (
            <div className="row between center" style={{ padding: '6px 0' }}>
              <div className="row center gap-8">
                <span style={{ fontSize: 13 }}>📄</span>
                <span style={{ fontSize: 14, color: ready ? 'var(--ink)' : 'var(--faint)' }}>{name}</span>
              </div>
              <div className="meta">{when}</div>
            </div>
          );
          return ready && doc?.pdfUrl ? (
            <a
              key={row.type}
              href={doc.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {body}
            </a>
          ) : (
            <div key={row.type}>{body}</div>
          );
        })}
        {/* Shipment label (carrier) — see SHIPPING_TRACKING_ENABLED */}
        {SHIPPING_TRACKING_ENABLED && <ShipmentLabelRow url={trackingDoc} />}
      </div>

      {/* "Resend proforma" / "Contact client" actions are not built yet — no placeholders. */}
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="row between">
      <span className="label">{label}</span>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}

function ShipmentLabelRow({ url }: { url: string | null }) {
  const t = useT(dict);
  const body = (
    <div className="row between center" style={{ padding: '6px 0' }}>
      <div className="row center gap-8">
        <span style={{ fontSize: 13 }}>📄</span>
        <span style={{ fontSize: 14, color: url ? 'var(--ink)' : 'var(--faint)' }}>{t('docShipmentLabel')}</span>
      </div>
      <div className="meta">{url ? t('docReady') : t('docPending')}</div>
    </div>
  );
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
      {body}
    </a>
  ) : (
    body
  );
}

/** Pipeline-pill tone per status (display-only; does not change with language). */
const STATUS_TONE: Record<OrderStatus, string> = {
  [OrderStatus.NEW]: 'blue',
  [OrderStatus.PROFORMA_SENT]: 'amber',
  [OrderStatus.ON_HOLD_ADMIN_REVIEW]: 'red',
  [OrderStatus.PAID]: 'green',
  [OrderStatus.INVOICED]: 'green',
  [OrderStatus.SHIPPED]: 'blue',
  [OrderStatus.DELIVERED]: 'green',
  [OrderStatus.DELIVERY_PROBLEM]: 'red',
};
